/* eslint-disable react-refresh/only-export-components --
 * Canonical dockview-react panel-registry pattern (mirrored
 * from Appaloosa-Core's :file:`src/workbook/panels/index.ts`):
 * co-locates the inner panel components with the registry
 * :data:`panelComponents` record they're aggregated into. The
 * record is the cross-stream contract Stream C consumes; the
 * fast-refresh-degrades-to-reload trade-off is acceptable for
 * a stable infrastructure module that changes only when new
 * panels register (rare; per-stream).
 */
/**
 * Panel component registry — maps each :type:`PanelId` to the
 * React component dockview-react renders inside that panel's
 * content area.
 *
 * Stream C PR 1 registered all primary surfaces per ADR-024 §2
 * with placeholders for streams that hadn't shipped real content
 * yet. (The ``allow-list`` surface was retired in the PR5a
 * cleanup.) As of Stream A PR 5.2 the inventory is:
 *
 *   - ``map`` — :class:`Map` (Stream A PR 2).
 *   - ``platforms`` — :class:`PlatformsPanel` (Stream A PR 5.2).
 *   - ``detections`` — :class:`DetectionsPanel` (REQ-UI-061; ADR-032).
 *   - ``alerts`` — :class:`AlertsPanel` (REQ-UI-062; ADR-029).
 *   - ``entity-details`` — :class:`EntityDetailsPanel` (Stream A PR 5.3).
 *   - ``audit-log`` — :class:`AuditLogPanel` (Stream G G2; REQ-UI-022,
 *     admin-only per the 2026-06-08 §6 ruling).
 *   - ``system-status`` — :class:`SystemStatusPanel` (Flow L; ADR-035 / S6,
 *     support_engineer + admin per REQ-STATUS-029; supersedes System Health).
 *   - ``onboarding`` / ``user-management`` — :class:`PlaceholderPanel`
 *     (pending their owning streams).
 *
 * (The data-plane ``events`` surface was removed in the ADR-032
 * cascade — PR6; ``detections`` + ``alerts`` supersede it.)
 *
 * The components map is passed to :class:`DockviewReact` as its
 * ``components`` prop; dockview uses each panel's
 * ``contentComponent`` field to look up the React component.
 *
 * Cross-references:
 *   - ADR-024 §2 (dockable surfaces inventory).
 *   - docs/attic/mvp/streams/C-multi-pane.md
 *     §"First-week plan".
 *   - Stream A PR 2 — :file:`src/map/Map.tsx`.
 */
import type { FunctionComponent } from "react";
import type { IDockviewPanelProps } from "dockview-react";
import { PanelVisibilityProvider } from "@/multi-pane/panel-visibility-provider";
import { AlertsPanel } from "@/alerts/alerts-panel";
import { AuditLogPanel } from "@/audit/audit-log-panel";
import { DetectionsPanel } from "@/detections/detections-panel";
import { TracksPanel } from "@/object-tracks/tracks-panel";
import { EntityDetailsPanel } from "@/entity-details/entity-details-panel";
import { Map as MapView } from "@/map/Map";
import { PlatformsPanel } from "@/platforms/platforms-panel";
import { QueryPanel } from "@/query/query-panel";
import { SystemStatusPanel } from "@/system-status/system-status-panel";
import { PANEL_TITLES, type PanelId } from "@/multi-pane/registry-types";
import { PlaceholderPanel } from "@/multi-pane/placeholder-panel";
import { ErrorBoundary } from "@/chrome/error-boundary";

/**
 * Wrap a dockview panel component in a render-error boundary (REQ-UI-191).
 *
 * A panel that throws while rendering — the 2026-10-07 case was MapLibre's
 * ``getStyle()`` returning ``undefined`` inside the Map theme effect — is
 * replaced by a failure card inside its own content area, named from
 * :data:`PANEL_TITLES` via ``props.api.id`` (the per-panel identity
 * contract :class:`PlaceholderPanel` also relies on). The tab header, the
 * other panels, the top bar and the SSE providers above the workspace keep
 * running; "Reload panel" remounts just this subtree. Without the wrapper
 * the exception climbed to the router and replaced the whole operator UI
 * with react-router's default error page.
 */
function withPanelErrorBoundary(
  Inner: FunctionComponent<IDockviewPanelProps>,
): FunctionComponent<IDockviewPanelProps> {
  function GuardedDockPanel(props: IDockviewPanelProps): React.ReactElement {
    const id = props.api.id as PanelId;
    const title = PANEL_TITLES[id] ?? id;
    return (
      <ErrorBoundary scope="panel" surface={title} testId={`panel-error-${id}`}>
        <Inner {...props} />
      </ErrorBoundary>
    );
  }
  GuardedDockPanel.displayName = `Guarded(${Inner.displayName ?? Inner.name})`;
  return GuardedDockPanel;
}

/**
 * Wrap the existing :class:`Map` view as a dockview panel.
 *
 * The Map component owns its own MapLibre instance + SSE
 * subscription; dockview only owns the bounding container
 * (sizing + drag handle + tab header). The Map's
 * ``data-testid="map-canvas"`` survives the wrapping for
 * Playwright + chrome.test assertions.
 *
 * dockview's ``IDockviewPanelProps`` exposes ``api``,
 * ``containerApi``, and ``params`` — the Map doesn't use any of
 * them today. Future work (drag-out, panel-level filter
 * preferences) consumes ``api`` for per-panel state.
 */
function MapDockPanel(_props: IDockviewPanelProps): React.ReactElement {
  return <MapView />;
}

/**
 * Wrap the :class:`PlatformsPanel` as a dockview panel.
 *
 * Same pattern as :func:`MapDockPanel` — the underlying component
 * is content-only (TanStack-Query-driven list with click-to-select
 * wiring); dockview owns the surrounding chrome.
 */
function PlatformsDockPanel(props: IDockviewPanelProps): React.ReactElement {
  // Thread the dockview panel api so the panel can re-centre its selected
  // row when this tab is shown after a Map selection made while it was
  // hidden (REQ-UI-051; PR #300 item 2). dockview keeps inactive tabs
  // mounted-but-``display:none``, so visibility is the only signal.
  return <PlatformsPanel panelApi={props.api} />;
}

/**
 * Wrap the :class:`DetectionsPanel` as a dockview panel (REQ-UI-061;
 * ADR-032 — the Detection successor to the removed data-plane Events
 * panel).
 *
 * Same content-only pattern as the others: the panel reads the shared
 * detections store (seeded by the global recent-detections feed + the
 * live SSE bridge) and originates ``detection``-kind selection into the
 * bus. Seeded into the Operations Center preset (the PR6 events→detections
 * swap) and reachable via the Add panel menu; default-hidden in the other
 * presets per ADR-024 §2.
 */
function DetectionsDockPanel(props: IDockviewPanelProps): React.ReactElement {
  // See PlatformsDockPanel: thread the api for show-to-scroll (REQ-UI-051).
  return <DetectionsPanel panelApi={props.api} />;
}

/**
 * Wrap the :class:`TracksPanel` as a dockview panel (ADR-041 D-5;
 * REQ-UI-170). Same content-only pattern: the panel reads the shared
 * object-track store (seeded by its own recent-tracks poll + the Map's
 * viewport backfill + the detections SSE bridge) and originates
 * ``track``-kind (row) and ``detection``-kind (member row) selection.
 */
function TracksDockPanel(props: IDockviewPanelProps): React.ReactElement {
  return <TracksPanel panelApi={props.api} />;
}

/**
 * Wrap the :class:`AlertsPanel` as a dockview panel (REQ-UI-062;
 * ADR-029 §6.2 / ADR-032).
 *
 * Same content-only pattern as the others: the panel reads the shared
 * alerts store (seeded by the global recent-alerts feed + the live SSE
 * bridge, which also applies the cross-session ``alert-dismissed``
 * fan-out) and originates ``alert``-kind selection into the bus. The
 * per-alert Dismiss control is operator/admin-gated inside the panel.
 * Default-hidden per ADR-024 §2; reachable via the Add panel menu (the
 * preset wiring is PR6).
 */
function AlertsDockPanel(props: IDockviewPanelProps): React.ReactElement {
  // See PlatformsDockPanel: thread the api for show-to-scroll (REQ-UI-051).
  return <AlertsPanel panelApi={props.api} />;
}

/**
 * Wrap the :class:`EntityDetailsPanel` as a dockview panel.
 *
 * Stream A PR 5.3 wires the consolidated Entity Details surface.
 * The panel reads from the selection store and renders one of:
 * empty state (no selection), Platform sections (kind="platform"),
 * or a "coming in PR 5.5" placeholder for Aggregator / Event
 * kinds.
 */
function EntityDetailsDockPanel(props: IDockviewPanelProps): React.ReactElement {
  // The api is supplied for VISIBILITY only (REQ-STATUS-099): the status
  // sections share the System Status queries, whose live-frame refresh must not
  // run for a pane nobody can see. Selection is still bus-driven, not
  // api-driven, so the panel itself takes no api prop.
  return (
    <PanelVisibilityProvider panelApi={props.api}>
      <EntityDetailsPanel />
    </PanelVisibilityProvider>
  );
}

/**
 * Wrap the :class:`AuditLogPanel` as a dockview panel (Stream G G2).
 *
 * Admin-only surface (Flow J / REQ-UI-022); the panel reads the BFF
 * admin audit-query endpoint and owns its filter bar + table + export
 * modal. dockview owns only the surrounding chrome; the panel does not
 * consume the panel ``api`` — Audit Log + System Status are independent
 * of the synchronized-selection bus per ADR-024 §5.
 */
function AuditLogDockPanel(_props: IDockviewPanelProps): React.ReactElement {
  return <AuditLogPanel />;
}

/**
 * Wrap the :class:`SystemStatusPanel` as a dockview panel (Flow L; ADR-035 / S6
 * — supersedes the retired System Health panel).
 *
 * support_engineer + admin surface (Flow L / REQ-STATUS-029); the panel reads
 * the BFF system-status snapshot + SSE stream (snapshot-then-SSE) + owns the
 * re-homed incident-disable action surface. It is independent of the
 * synchronized-selection bus per ADR-024 §5 / REQ-UI-051, but it DOES consume
 * the panel ``api`` — for **visibility** (REQ-STATUS-099).
 *
 * That matters more here than anywhere else: this panel is in the default
 * layout (:file:`./presets`) behind the map in a tab group, so dockview keeps it
 * mounted in essentially every session. Without the api it cannot tell "the
 * operator is looking at me" from "I happen to be mounted", and its freshness
 * refresh runs in every authenticated tab.
 */
function SystemStatusDockPanel(props: IDockviewPanelProps): React.ReactElement {
  return (
    <PanelVisibilityProvider panelApi={props.api}>
      <SystemStatusPanel />
    </PanelVisibilityProvider>
  );
}

/**
 * Wrap the :class:`QueryPanel` as a dockview panel (Flow X; ADR-038
 * §Decision 5).
 *
 * Readable by every authenticated role (REQ-AUTH-075) — the disclosure
 * bound is the BFF's role-filtered catalog and re-validating compiler, not
 * the visibility of the panel. Like Audit Log and System Status it does
 * not consume the panel ``api``: the Query panel *originates* selection
 * into the ``inspected`` slot (Phase G) but is never a highlight target,
 * so it has no show-to-scroll obligation.
 */
function QueryDockPanel(_props: IDockviewPanelProps): React.ReactElement {
  return <QueryPanel />;
}

/**
 * The component map dockview-react consumes via its
 * ``components`` prop. Keys must match the ``contentComponent``
 * field of each panel descriptor in the
 * :type:`SerializedDockview.panels` map and in the seeded preset
 * layouts (see :file:`./presets.ts`).
 */
export const panelComponents: Record<PanelId, FunctionComponent<IDockviewPanelProps>> = {
  map: withPanelErrorBoundary(MapDockPanel),
  query: withPanelErrorBoundary(QueryDockPanel),
  platforms: withPanelErrorBoundary(PlatformsDockPanel),
  detections: withPanelErrorBoundary(DetectionsDockPanel),
  tracks: withPanelErrorBoundary(TracksDockPanel),
  alerts: withPanelErrorBoundary(AlertsDockPanel),
  "audit-log": withPanelErrorBoundary(AuditLogDockPanel),
  onboarding: withPanelErrorBoundary(PlaceholderPanel as FunctionComponent<IDockviewPanelProps>),
  "user-management": withPanelErrorBoundary(
    PlaceholderPanel as FunctionComponent<IDockviewPanelProps>,
  ),
  "system-status": withPanelErrorBoundary(SystemStatusDockPanel),
  "entity-details": withPanelErrorBoundary(EntityDetailsDockPanel),
};
